import { send, SendFeedbackModel } from "client/Api/Feedback/feedback";
import {
	Checkbox,
	Dialog,
	Form,
	FormItem,
	TextArea,
	TextInput,
	Toolbar,
	ValidationError
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog,
	useUnmountedCancellation
} from "client/Hooks";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import { isNotEmpty } from "client/Validation/rules";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import {
	ReactElement,
	useCallback,
	useState
} from "react";

export type FeedbackDialogProps = {
	onClose: () => void;
};

export const FeedbackDialog = (props: FeedbackDialogProps): ReactElement => {
	const {
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [formData, setFormData] = useState<SendFeedbackModel>({
		canContactMe: true,
		message: ""
	});

	const formHandler: IFormHandler<SendFeedbackModel> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const submitHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			const { validation } = formHandler;
			try {
				const validationResult: InputValidationResult = await validation.validate(globalCancellation);
				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				await send(formData, globalCancellation);

				alertManager.showSuccess(
					localize("_FeedbackSuccessfullySend"),
				);

				onClose();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!validation.tryProcessServerError(e)) {
					console.error(e);
					alertManager.showError(localize("_FailedToSendFeedback"));
				}

				setIsBusy(false);
			}
		},
		[
			alertManager,
			formData,
			formHandler,
			globalCancellation,
			isBusy,
			onClose
		]
	);

	return (
		<Dialog trackingId="feedback-dialog" hasBusyIndicator={isBusy}>
			<Dialog.Header
				title={localize("_GiveFeedback")}
				onClose={onClose}
			/>

			<Dialog.Body>
				<Form onSubmit={submitHandler}>
					<FormItem label={localize("_Feedback")} isRequired>
						<TextArea
							{...formHandler.for("message").attachToTextArea()}
							rows={6}
							placeholder={localize("_FeedbackTextareaPlaceholder")}
						/>

						<ValidationError {...formHandler.for("message").attachToValidationError()} />
					</FormItem>

					<FormItem>
						<Checkbox
							{...formHandler.for("canContactMe").attachToCheckbox()}
							text={localize("_ContactMeIfNeeded")}
						/>

						<ValidationError {...formHandler.for("canContactMe").attachToValidationError()} />
					</FormItem>

					{formData.canContactMe && (
						<FormItem label={localize("_EmailAddressToContact")}>
							<TextInput
								value={userConfiguration().emailAddress}
								trackingId="email-address"
								isDisabled
							/>
						</FormItem>
					)}
				</Form>
			</Dialog.Body>

			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_Send")}
						onClick={submitHandler}
						trackingId="send"
					/>

					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
FeedbackDialog.displayName = "FeedbackDialog";

const validationConfiguration: IValidationConfiguration<SendFeedbackModel> = createValidationConfiguration<SendFeedbackModel>();

validationConfiguration.forScalar("message")
	.add(
		isNotEmpty()
	);

export function useFeedbackDialog(): [
	element: ReactElement | null,
	open: () => Promise<void>
] {
	const [element, originalOpen] = useDataInputDialog<{}, void>(
		(_props, onClose) => (
			<FeedbackDialog
				onClose={onClose}
			/>
		)
	);

	const open = useCallback(
		async (): Promise<void> => {
			await originalOpen({});
		},
		[originalOpen]
	);

	return [element, open];
}
